import { Injectable, computed, signal } from '@angular/core';
import { StorageService } from './storage.service';

/**
 * Mirrors the subset of the real product shape app-product-card binds to
 * (final_price/price/image/slug/shop/stock), so recently-viewed items can be
 * rendered through the same shared card with no adapter/mapping layer.
 */
export interface RecentlyViewedItem {
  id: number;
  slug: string | null;
  name: string;
  image: string;
  price: number;
  final_price: number;
  shop?: { name?: string };
  stock?: number;
  is_in_stock?: boolean;
  viewedAt: number;
}

const RECENTLY_VIEWED_KEY = 'recently_viewed';
const MAX_ITEMS = 12;

/**
 * No backend "recently viewed" tracker exists - this persists to localStorage,
 * same pattern already used for the guest cart and WishlistService. Most
 * recent first, capped at MAX_ITEMS, deduplicated by product id.
 */
@Injectable({ providedIn: 'root' })
export class RecentlyViewedService {
  private itemsSignal = signal<RecentlyViewedItem[]>([]);
  readonly items = this.itemsSignal.asReadonly();
  readonly count = computed(() => this.itemsSignal().length);

  constructor(private storage: StorageService) {
    this.itemsSignal.set(this.storage.getItemA(RECENTLY_VIEWED_KEY) || []);
  }

  record(product: any): void {
    if (!product?.id) return;

    const item: RecentlyViewedItem = {
      id: product.id,
      slug: product.slug || null,
      name: product.name,
      image: product.image,
      price: Number(product.price) || 0,
      final_price: Number(product.final_price ?? product.price) || 0,
      shop: product.shop?.name ? { name: product.shop.name } : undefined,
      stock: product.stock,
      is_in_stock: product.is_in_stock,
      viewedAt: Date.now(),
    };

    const withoutExisting = this.itemsSignal().filter(i => i.id !== item.id);
    const updated = [item, ...withoutExisting].slice(0, MAX_ITEMS);
    this.itemsSignal.set(updated);
    this.storage.setItemA(RECENTLY_VIEWED_KEY, updated);
  }
}
